import React, { memo } from 'react';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import PropTypes from 'prop-types';

const formatNumericValue = (value) => {
  if (value == null || value === '') return '-';
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '-';
  if (Number.isInteger(numberValue)) return String(numberValue);
  return numberValue.toFixed(2).replace(/\.?0+$/, '');
};

const getPriceValue = (row) =>
  row?.nonStockPrice ??
  row?.price ??
  row?.sellingPrice ??
  row?.salePrice ??
  row?.unitPrice ??
  row?.retailPrice;

const getAvailabilityValue = (row) => {
  if (row?.nonStockProductAvailability !== undefined && row?.nonStockProductAvailability !== null) {
    return row.nonStockProductAvailability ? 'Available' : 'Unavailable';
  }
  if (typeof row?.availability === 'boolean') {
    return row.availability ? 'Available' : 'Unavailable';
  }
  if (typeof row?.availability === 'string' && row.availability.trim()) {
    return row.availability;
  }
  if (typeof row?.status === 'string' && row.status.trim()) {
    return row.status;
  }
  const stockValue = Number(row?.stockAmount);
  return Number.isFinite(stockValue) && stockValue > 0 ? 'Available' : '-';
};

const formatStockValue = (row) => {
  const stockValue = Number(row?.stockAmount);
  const hasNonStockMeta =
    row?.nonStockPrice !== undefined && row?.nonStockPrice !== null
      ? true
      : row?.nonStockProductAvailability !== undefined && row?.nonStockProductAvailability !== null;

  if (Number.isFinite(stockValue) && stockValue === 0 && hasNonStockMeta) {
    return '-';
  }
  return formatNumericValue(row?.stockAmount);
};

const collapsibleColumns = [
  { id: 'code', label: 'Code', minWidth: 80 },
  { id: 'variantName', label: 'Variant', minWidth: 120 },
  { id: 'barcode', label: 'Barcode', minWidth: 100 },
  { id: 'categoryName', label: 'Category', minWidth: 100 },
  {
    id: 'stockAmount',
    label: 'Stock',
    minWidth: 80,
    align: 'right',
    mono: true,
    format: (_, row) => formatStockValue(row),
  },
  {
    id: 'nonStockPrice',
    label: 'Price',
    minWidth: 90,
    align: 'right',
    mono: true,
    format: (_, row) => formatNumericValue(getPriceValue(row)),
  },
  {
    id: 'nonStockProductAvailability',
    label: 'Availability',
    minWidth: 110,
    format: (_, row) => getAvailabilityValue(row),
  },
  {
    id: 'options',
    minWidth: 20,
    options: ['Update'],
    table: 'product',
  },
];

const ProductTable = memo(({
  rows = [],
  totalCount = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  onViewImage,
  loading = false,
}) => {
  const columns = [
    {
      id: 'code',
      label: 'Code',
      minWidth: 50,
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
    },
    {
      id: 'barcode',
      label: 'Barcode',
      minWidth: 100,
    },
    {
      id: 'categoryName',
      label: 'Category',
      minWidth: 100,
    },
    {
      id: 'stockAmount',
      label: 'Stock',
      minWidth: 100,
      align: 'right',
      mono: true,
      render: (_, row) => formatStockValue(row),
    },
    {
      id: 'price',
      label: 'Price',
      minWidth: 100,
      align: 'right',
      mono: true,
      render: (_, row) => formatNumericValue(getPriceValue(row)),
    },
    {
      id: 'availability',
      label: 'Availability',
      minWidth: 120,
      render: (_, row) => getAvailabilityValue(row),
    },
    {
      id: 'options',
      minWidth: 20,
      options: ['View', 'Update'],
      table: 'product',
    },
  ];

  return (
    <div className={styles.mainDiv}>
      {loading && <Loading variant="overlay" label="Loading..." />}
      <Table
        columns={columns}
        rows={rows}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        totalCount={totalCount}
        disableInternalPagination
        collapsibleColumns={collapsibleColumns}
        collapsibleTitle="Variants"
        onViewImage={onViewImage}
        getRowChildren={(row) =>
          Array.isArray(row.childProducts)
            ? row.childProducts.map((child) => ({
                ...child,
                categoryName: child?.categoryName ?? row?.categoryName,
                nonStockPrice: child?.nonStockPrice ?? row?.nonStockPrice,
                nonStockProductAvailability:
                  child?.nonStockProductAvailability ?? row?.nonStockProductAvailability,
              }))
            : []
        }
      />
    </div>
  );
});

ProductTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  totalCount: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewImage: PropTypes.func,
  loading: PropTypes.bool,
};

ProductTable.defaultProps = {
  rows: [],
  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  onViewImage: null,
  loading: false,
};

export default ProductTable;
